import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTwilio } from '../../contexts/TwilioContext';
import IncomingCallModal from '../modals/IncomingCallModal';
import AgentSelectionModal from '../modals/AgentSelectionModal';
import MultipleIncomingCalls from '../multiple-incoming-calls';
import { PhoneIcon } from '../icons/phone-icon';
import styles from './twilio-call-handler.module.css';

const TwilioCallHandler = () => {
  const navigate = useNavigate();
  const {
    incomingCall,
    activeCall,
    activeCalls,
    isMuted,
    acceptCall,
    rejectCall,
    hangUp,
    toggleMute,
    transferCall,
    // ✨ Nuevas propiedades para múltiples llamadas
    totalIncomingCalls,
    hasMultipleIncoming,
    incomingCalls,
    isInitiatingOutgoingCall, // ✨ NUEVO: Estado para llamadas salientes
    consultation,
    completeWarmTransfer,
    cancelWarmConsultation,
    // ✨ Estados para transferencia
    showAgentSelectionModal,
    availableAgents,
    allAgents, // 📋 Todos los agentes para lookup de nombres
    handleAgentSelection,
    handleCancelAgentSelection
  } = useTwilio();

  // ⏱️ Timer de duración de llamada activa
  const [callDuration, setCallDuration] = useState(0);
  const [showKeypad, setShowKeypad] = useState(false);
  const [dtmfInput, setDtmfInput] = useState('');
  const activeCallInfo = activeCalls?.find(c => c.call === activeCall) || null;

  // Enviar dígito DTMF
  const sendDtmf = (digit) => {
    if (!activeCall) return;
    try {
      activeCall.sendDigits(digit);
      setDtmfInput(prev => (prev + digit).slice(-12)); // Máx 12 dígitos visibles
      console.log('🔢 DTMF enviado:', digit);
    } catch (e) {
      console.error('❌ Error enviando DTMF:', e);
    }
  };

  useEffect(() => {
    if (!activeCall || !activeCallInfo) {
      setCallDuration(0);
      setShowKeypad(false);
      setDtmfInput('');
      return;
    }
    const startTime = activeCallInfo.timestamp ? activeCallInfo.timestamp.getTime() : Date.now();
    // Calcular duración inicial por si ya lleva tiempo
    setCallDuration(Math.floor((Date.now() - startTime) / 1000));
    const interval = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [activeCall]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // ✨ Componente para múltiples llamadas entrantes
  if (hasMultipleIncoming) {
    return <MultipleIncomingCalls />;
  }

  // Modal para llamada entrante individual (solo si NO estamos iniciando una saliente)
  if (incomingCall && totalIncomingCalls === 1 && !isInitiatingOutgoingCall) {
    // Buscar información del contacto en la lista de llamadas entrantes
    const currentCallInfo = incomingCalls.find(call => call.call === incomingCall);
    
    // Procesar el "from" para detectar si es una llamada de agente
    let displayFrom = incomingCall.parameters.From;
    let agentContactInfo = null;
    
    if (displayFrom && displayFrom.startsWith('client:')) {
      const username = displayFrom.replace('client:', '');
      const callingAgent = allAgents.find(a => 
        a.email && a.email.split('@')[0] === username
      );
      
      if (callingAgent) {
        // Crear contactInfo para el agente
        agentContactInfo = {
          name: callingAgent.nombre,
          nombre: callingAgent.nombre,
          type: 'Compañera',
          tipo: 'Compañera',
          phone: username,
          isAgent: true
        };
        displayFrom = callingAgent.nombre;
      } else {
        displayFrom = username;
      }
    }
    
    return (
      <IncomingCallModal
        open={true}
        from={displayFrom}
        contactInfo={agentContactInfo || currentCallInfo?.contactInfo || null}
        onAccept={acceptCall}
        onReject={rejectCall}
      />
    );
  }

  // Panel de control para llamada activa (flotante)
  if (activeCall) {
    const callStatus = activeCall.status?.();
    const isConnected = callStatus === 'open';
    
    // Determinar qué mostrar como número/nombre
    let displayNumber = 'Número desconocido';
    
    // 1. Si es llamada saliente, usar _destinationNumber
    if (activeCall._destinationNumber) {
      displayNumber = activeCall._destinationNumber;
      
      // Si es una llamada a otro agente (no empieza con +), buscar el nombre
      if (!displayNumber.startsWith('+')) {
        // Es un username de agente, buscar su nombre
        const targetAgent = availableAgents.find(a => 
          a.email && a.email.split('@')[0] === displayNumber
        );
        if (targetAgent) {
          displayNumber = targetAgent.nombre || displayNumber;
        }
      }
    }
    // 2. Si es llamada entrante, revisar From
    else if (activeCall.parameters?.From) {
      const from = activeCall.parameters.From;
      
      // Si viene de otro agente (client:xxx), extraer nombre
      if (from.startsWith('client:')) {
        const username = from.replace('client:', '');
        console.log('🔍 Buscando agente que llama:', username);
        console.log('🔍 Todos los agentes:', allAgents);
        
        // Buscar en TODOS los agentes (no solo disponibles)
        const callingAgent = allAgents.find(a => 
          a.email && a.email.split('@')[0] === username
        );
        
        if (callingAgent) {
          displayNumber = callingAgent.nombre;
          console.log('✅ Agente encontrado:', callingAgent.nombre);
        } else {
          // Si no está en allAgents, usar el username
          displayNumber = username;
          console.log('⚠️ Agente no encontrado en lista, usando username:', displayNumber);
        }
      } else {
        // Es un número de teléfono normal
        displayNumber = from;
      }
    }
    // 3. Fallback a To si no hay From
    else if (activeCall.parameters?.To) {
      displayNumber = activeCall.parameters.To;
    }
    
    const contactInfo = activeCallInfo?.contactInfo || null;

    return (
      <>
        <div className={styles.activeCallPanel}>
          <div className={styles.callInfo}>
            <div className={styles.activePhoneIconContainer}>
              <PhoneIcon className={styles.activePhoneIcon} />
            </div>
            <div className={styles.callDetails}>
              <span className={styles.callStatus}>
                {isConnected ? 'EN LLAMADA' : 'Conectando...'}
              </span>
              {/* Nombre del contacto (si identificado) */}
              {contactInfo?.isKnown && contactInfo.name ? (
                <span className={styles.callContactName}>{contactInfo.name}</span>
              ) : (
                <span className={styles.callNumber}>{displayNumber}</span>
              )}
              {/* Número real si hay nombre */}
              {contactInfo?.isKnown && (
                <span className={styles.callPhoneSubtitle}>{displayNumber}</span>
              )}
              {/* Expediente (clickable) */}
              {contactInfo?.expediente && (
                <span
                  className={styles.callExpediente}
                  onClick={() => contactInfo.expedienteId && navigate(`/record/${contactInfo.expedienteId}`)}
                  title={contactInfo.expedienteId ? 'Abrir expediente' : ''}
                >
                  📁 {contactInfo.expediente}
                  {contactInfo.details && contactInfo.details !== contactInfo.expediente && (
                    <span className={styles.callExpedienteTitle}> — {contactInfo.details.replace(contactInfo.expediente, '').replace(/^ - /, '')}</span>
                  )}
                </span>
              )}
              {/* Duración */}
              {isConnected && (
                <span className={styles.callDuration}>{formatDuration(callDuration)}</span>
              )}
            </div>
          </div>
          {consultation && (
            <div className={styles.consultationBanner} role="status">
              <strong>
                {consultation.phase === 'calling' || consultation.phase === 'ringing'
                  ? `Llamando a ${consultation.agentName}…`
                  : consultation.phase === 'completing'
                    ? 'Conectando al cliente…'
                    : `Consulta privada con ${consultation.agentName}`}
              </strong>
              <span>El cliente está en espera en silencio.</span>
              <div className={styles.consultationActions}>
                {consultation.phase === 'connected' && (
                  <button onClick={completeWarmTransfer} className={styles.completeTransferButton}>
                    Conectar cliente
                  </button>
                )}
                {consultation.phase !== 'completing' && (
                  <button onClick={cancelWarmConsultation} className={styles.cancelConsultationButton}>
                    Cancelar consulta
                  </button>
                )}
              </div>
            </div>
          )}
          {/* Teclado DTMF */}
          {showKeypad && isConnected && (
            <div className={styles.keypad}>
              <div className={styles.dtmfDisplay}>
                <span className={styles.dtmfText}>{dtmfInput || <span className={styles.dtmfPlaceholder}>Marcar extensión...</span>}</span>
                <button className={styles.dtmfClear} onClick={() => setDtmfInput('')} title="Borrar">⌫</button>
              </div>
              <div className={styles.keypadGrid}>
                {['1','2','3','4','5','6','7','8','9','*','0','#'].map(d => (
                  <button
                    key={d}
                    className={`${styles.keypadKey} ${d === '*' || d === '#' ? styles.keypadSpecial : ''}`}
                    onClick={() => sendDtmf(d)}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className={styles.callControls}>
            {consultation ? null : isConnected ? (
              // Controles para llamada conectada
              <>
                <button
                  onClick={toggleMute}
                  className={`${styles.controlButton} ${isMuted ? styles.muted : styles.unmuted}`}
                  title={isMuted ? "Activar micrófono" : "Silenciar micrófono"}
                >
                  {isMuted ? "🔇" : "🎤"}
                </button>
                
                <button
                  onClick={() => { setShowKeypad(v => !v); setDtmfInput(''); }}
                  className={`${styles.controlButton} ${showKeypad ? styles.keypadActive : styles.keypadButton}`}
                  title="Teclado numérico"
                >
                  #️⃣
                </button>

                <button
                  onClick={transferCall}
                  className={`${styles.controlButton} ${styles.transferButton}`}
                  title="Transferir llamada"
                >
                  📞➡️
                </button>
                
                <button
                  onClick={hangUp}
                  className={`${styles.controlButton} ${styles.hangUpButton}`}
                  title="Colgar"
                >
                  📞❌
                </button>
              </>
            ) : (
              // Controles para llamada conectando
              <>
                <button
                  onClick={hangUp}
                  className={`${styles.controlButton} ${styles.hangUpButton}`}
                  title="Cancelar llamada"
                >
                  📞❌
                </button>
                <span className={styles.connectingText}>Conectando...</span>
              </>
            )}
          </div>
        </div>
        
        {/* Modal de selección de agentes para transferencia */}
        <AgentSelectionModal
          open={showAgentSelectionModal}
          agents={availableAgents}
          onSelectAgent={handleAgentSelection}
          onCancel={handleCancelAgentSelection}
        />
      </>
    );
  }
  
  return (
    <>
      {/* Modal de selección de agentes para transferencia */}
      <AgentSelectionModal
        open={showAgentSelectionModal}
        agents={availableAgents}
        onSelectAgent={handleAgentSelection}
        onCancel={handleCancelAgentSelection}
      />
    </>
  );
};

export default TwilioCallHandler;